import { useItems } from '@api/generics';
import QueryKeys from '@api/index';
import API_ROUTES from '@api/routes';
import { useTenants } from '@api/tenants';
import { ItemsAutocomplete, MappingsRow } from '@components/Forms';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, useTheme } from '@mui/material';
import { uniqBy } from 'lodash';
import React, { useCallback } from 'react';
import { IRole, IRolesTenantsMapping } from 'types/authorization';
import { ITenant } from 'types/tenants';
import { ITheme } from 'types/theme';

interface IRolesTenantsMappingsProps {
	formik: any;
	id: string;
	disabled?: boolean;
}

const RolesTenantsMappings: React.FC<IRolesTenantsMappingsProps> = ({
	formik,
	id,
	disabled
}) => {
	const theme = useTheme<ITheme>();

	const mappings = React.useMemo<IRolesTenantsMapping[]>(
		() => formik.values[id] || [],
		[formik.values, id]
	);

	const { data: tenantsData, isLoading: isTenantsLoading } = useTenants();
	const { data: rolesData, isLoading: isRolesLoading } = useItems<IRole>(
		QueryKeys.roles,
		API_ROUTES.v1.roles.all
	);

	const handleAddMappings = useCallback(async () => {
		await formik.setFieldValue(id, [
			...mappings,
			{
				tenants: [],
				roles: []
			} as IRolesTenantsMapping
		]);
	}, [formik, id, mappings]);

	const handleSetMappings = useCallback(
		async (index: number, tenants: ITenant[], roles: IRole[]) => {
			const newMappings = mappings.map((mapping, i) => {
				if (i === index) {
					return {
						tenants,
						roles
					};
				}
				return mapping;
			});
			await formik.setFieldValue(id, newMappings);
		},
		[formik, id, mappings]
	);

	const handleDeleteMappings = useCallback(
		(index: number) => () => {
			const newMappings = mappings.filter((_, i) => i !== index);
			formik.setFieldValue(id, newMappings);
		},
		[formik, id, mappings]
	);

	const roles = rolesData?.data || [];

	// filter tenants - selected tenants should not appear in the autocomplete
	const filteredTenants = React.useMemo(() => {
		const tenants = tenantsData?.tenants || [];
		const selectedTenants = mappings.reduce(
			(acc, mapping) => [...acc, ...mapping.tenants],
			[] as ITenant[]
		);
		return tenants.filter(
			(tenant) => !selectedTenants.find((t) => t.id === tenant.id)
		);
	}, [mappings, tenantsData?.tenants]);

	return isTenantsLoading || isRolesLoading ? null : (
		<>
			{mappings.map((mapping, i: number) => {
				const key = i.toString();
				return (
					<Box
						pt={2}
						pb={2}
						pl={2}
						mb={2}
						key={key}
						border={1}
						borderColor={theme.palette.divider}
					>
						<MappingsRow
							disabled={disabled}
							leftComponent={
								<ItemsAutocomplete
									id={`tenants-${key}`}
									disabled={disabled}
									multiple
									values={mapping.tenants || []}
									data={uniqBy(
										[...filteredTenants, ...(mapping.tenants || [])],
										'id'
									)}
									textLabel={intl.tr('mappings.dialog.input.tenants.label')}
									onChange={async (v) => {
										await handleSetMappings(i, v, mapping.roles || []);
									}}
								/>
							}
							rightComponent={
								<ItemsAutocomplete
									id={`roles-${key}`}
									multiple
									disabled={disabled}
									values={mapping.roles || []}
									data={roles || []}
									textLabel={intl.tr('mappings.dialog.input.roles.label')}
									onChange={async (v) => {
										await handleSetMappings(i, mapping.tenants || [], v);
									}}
								/>
							}
							onDelete={handleDeleteMappings(i)}
						/>
					</Box>
				);
			})}
			{!disabled && (
				<Box mt={2}>
					<Button
						color="primary"
						startIcon={<AddIcon />}
						onClick={handleAddMappings}
					>
						{intl.tr('dialog.rows.add.button.label')}
					</Button>
				</Box>
			)}
		</>
	);
};

RolesTenantsMappings.defaultProps = {
	disabled: false
};

export default RolesTenantsMappings;
